import './style.scss'

import React, {
    JSX, useEffect, useRef, useState,
} from 'react'

import { ButtonColor } from '~/components/buttons/buttonColor'
import {
    getGoogleGoalContext, GOOGLE_EVENTS, sendGoogleGoal, setGoogleGoalContext,
} from '~/libs/analytic/ga'
import {
    ADS_CTA_QUERY_PARAM,
    CTA_CONFIG_BY_VARIANT,
    DEMO_PAGE_URL,
    getMobileCtaQueryValueFromUrl,
    getPresentationRegionFromUrl,
    LINKEDIN_COMPANY_URL,
    MobileCtaAction,
    MobileCtaVariant,
    PRESENTATION_REGION_CONTEXT_KEY,
    PresentationRegion,
    resolveMobileCtaVariant,
    resolvePresentationRegionByValue,
    useStoredAdsCtaVariant,
} from '~/libs/mobileCta'

import LinkedinIcon from '../../../../assets/images/icons/linkedin.svg?react'
import FlightPathBottom from '../../assets/img/banner/flight-path-bottom.svg?react'
import FlightPathMiddle from '../../assets/img/banner/flight-path-middle.svg?react'
import FlightPathTop from '../../assets/img/banner/flight-path-top.svg?react'
import bannerArt from '../../assets/img/banner/main-banner-art.webp'
import bannerArtSmall from '../../assets/img/banner/main-banner-art-480.webp'
import bannerArtMobile from '../../assets/img/banner/main-banner-art-768.webp'

type Props = {
    openModal: () => void
    openPdfModal: () => void
    url?: string
}

const sendRegionalPresentationOpenGoalByPalette = (region: PresentationRegion | '', isColorPalette: boolean): boolean => {
    if (region === PresentationRegion.europa) {
        sendGoogleGoal(
            isColorPalette
                ? GOOGLE_EVENTS.click_pdf_main_banner_ads_color_europa
                : GOOGLE_EVENTS.click_pdf_main_banner_ads_europa,
        )
        return true
    }

    if (region === PresentationRegion.latan) {
        sendGoogleGoal(
            isColorPalette
                ? GOOGLE_EVENTS.click_pdf_main_banner_ads_color_latan
                : GOOGLE_EVENTS.click_pdf_main_banner_ads_latan,
        )
        return true
    }

    if (region === PresentationRegion.mina) {
        sendGoogleGoal(
            isColorPalette
                ? GOOGLE_EVENTS.click_pdf_main_banner_ads_color_mina
                : GOOGLE_EVENTS.click_pdf_main_banner_ads_mina,
        )
        return true
    }

    return false
}

export function SectionBanner({ openModal, openPdfModal, url }: Props): JSX.Element {
    const initialMobileCtaQueryValueRef = useRef<string>(getMobileCtaQueryValueFromUrl(url))
    const initialPresentationRegionRef = useRef<PresentationRegion | ''>(getPresentationRegionFromUrl(url))
    const mobileCtaVariant = useStoredAdsCtaVariant(resolveMobileCtaVariant(url))
    const [presentationRegion, setPresentationRegion] = useState<PresentationRegion | ''>(initialPresentationRegionRef.current)

    const isExclusiveMediaOffer = mobileCtaVariant === MobileCtaVariant.free6
    const isColorAdsCta = mobileCtaVariant === MobileCtaVariant.start_free_ads_color
        || mobileCtaVariant === MobileCtaVariant.free6
    const isPrimaryButtonBlue = isExclusiveMediaOffer ? false : isColorAdsCta
    const isPrimaryButtonOrange = isExclusiveMediaOffer ? true : !isColorAdsCta
    const currentCtaConfig = mobileCtaVariant !== MobileCtaVariant.default
        ? CTA_CONFIG_BY_VARIANT[mobileCtaVariant]
        : CTA_CONFIG_BY_VARIANT[MobileCtaVariant.default]
    const presentationCtaConfig = CTA_CONFIG_BY_VARIANT[MobileCtaVariant.pdf]

    useEffect(() => {
        const nextGoalContext: Record<string, string> = {}

        if (initialMobileCtaQueryValueRef.current) {
            nextGoalContext[ADS_CTA_QUERY_PARAM] = initialMobileCtaQueryValueRef.current
        }

        if (initialPresentationRegionRef.current) {
            nextGoalContext[PRESENTATION_REGION_CONTEXT_KEY] = initialPresentationRegionRef.current
        }

        if (Object.keys(nextGoalContext).length > 0) {
            setGoogleGoalContext(nextGoalContext)
        }

        const goalContext = getGoogleGoalContext()
        const presentationRegionFromContext = goalContext[PRESENTATION_REGION_CONTEXT_KEY]
        setPresentationRegion(
            initialPresentationRegionRef.current || resolvePresentationRegionByValue(
                typeof presentationRegionFromContext === 'string' ? presentationRegionFromContext : '',
            ),
        )
    }, [])

    const handleCtaClick = (variant: MobileCtaVariant) => {
        const { event, action } = CTA_CONFIG_BY_VARIANT[variant]

        if (action === MobileCtaAction.open_pdf_modal) {
            const isRegionalGoalSent = sendRegionalPresentationOpenGoalByPalette(presentationRegion, isColorAdsCta)
            if (!isRegionalGoalSent) {
                sendGoogleGoal(isColorAdsCta ? GOOGLE_EVENTS.click_pdf_main_banner_ads_color : event)
            }
            openPdfModal()
            return
        }

        sendGoogleGoal(event)

        if (action === MobileCtaAction.open_linkedin) {
            window.open(LINKEDIN_COMPANY_URL, '_blank', 'noopener')
            return
        }

        if (action === MobileCtaAction.open_demo) {
            window.location.href = DEMO_PAGE_URL
            return
        }

        openModal()
    }

    return (
        <>
            {isExclusiveMediaOffer && (
                <div className="main-banner__promo-bar">
                    Exclusive media offer — This page is only accessible via a special link. Free access is guaranteed for 6 months.
                </div>
            )}
            <div className={`main-banner ${isExclusiveMediaOffer ? 'main-banner_offer' : ''}`}>
                <div className="main-banner__sky" aria-hidden="true">
                    <FlightPathTop className="main-banner__flight-path main-banner__flight-path_top" />
                    <FlightPathMiddle className="main-banner__flight-path main-banner__flight-path_middle" />
                    <FlightPathBottom className="main-banner__flight-path main-banner__flight-path_bottom" />
                </div>
                <div className="main-banner__container">
                    <div className="main-banner__content">
                        <div className="main-banner__left">
                            <div className="main-banner__eyebrow">/ FOR BROKERS • FORWARDERS • SHIPPERS • AIRLINES</div>
                            <h1 className="main-banner__title">
                                Air Cargo Charter
                                {' '}
                                <br />
                                Marketplace
                            </h1>
                            {isExclusiveMediaOffer ? (
                                <div className="main-banner__offer">
                                    <div className="main-banner__offer-title">Register now.</div>
                                    <div className="main-banner__offer-text">Lock in 6 months free — guaranteed.</div>
                                </div>
                            ) : (
                                <div className="main-banner__text-top">
                                    Find aircraft, sell available capacity and book cargo charters worldwide — in hours, not days
                                </div>
                            )}
                            <div className="main-banner__mobile-cta">
                                <div className="main-banner__mobile-cta-buttons">
                                    <ButtonColor
                                        text={currentCtaConfig.text}
                                        modClass="main-banner__mobile-cta-button"
                                        onClick={() => handleCtaClick(mobileCtaVariant)}
                                        blue={isPrimaryButtonBlue}
                                        orange={isPrimaryButtonOrange}
                                        SvgIcon={currentCtaConfig.action === MobileCtaAction.open_linkedin ? LinkedinIcon : null}
                                    />
                                    {!isExclusiveMediaOffer && (
                                        <ButtonColor
                                            text={presentationCtaConfig.text}
                                            modClass="main-banner__mobile-cta-button main-banner__mobile-cta-button_secondary"
                                            onClick={() => handleCtaClick(MobileCtaVariant.pdf)}
                                            blue
                                        />
                                    )}
                                    {isExclusiveMediaOffer && (
                                        <div className="main-banner__mobile-cta-note">
                                            No credit card required.
                                            <br />
                                            <strong>This offer is only available via this link.</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <picture className="main-banner__art">
                            <source
                                media="(max-width: 575px)"
                                srcSet={`${bannerArtSmall} 480w, ${bannerArtMobile} 768w`}
                                sizes="calc(100vw - 36px)"
                            />
                            <img src={bannerArt} alt="" width={1216} height={874} fetchPriority="high" decoding="async" />
                        </picture>
                    </div>
                </div>
                <div className="main-banner__stats">
                    <div className="main-banner__stat">
                        <strong>190+</strong>
                        <span>Freighter Aircraft</span>
                        <small>Europe — CIS — MENA — Asia — Americas</small>
                    </div>
                    <div className="main-banner__stat">
                        <strong>500+</strong>
                        <span>Empty Legs</span>
                        <small>Direct from Cargo Operators</small>
                    </div>
                    <div className="main-banner__stat">
                        <strong>300+</strong>
                        <span>Freight Forwarders & Brokers</span>
                        <small>Verified Users</small>
                    </div>
                </div>
            </div>
        </>
    )
}
